// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { normalizeDateTime } from '@/utilities/date-time';
import { DateTime } from 'luxon';
import { z } from 'zod';

const unassignAssetsResponseSchema = z
  .object({
    data: z.object({ success: z.boolean() }),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    success: data.success,
  }));

type UnassignAssetsResponse = z.infer<typeof unassignAssetsResponseSchema>;

interface AssetUnassignment {
  assetId: string;
  date: DateTime;
}

/**
 * Removes assets from a project in the remote API.
 *
 * @param key - The key of the project for which the assets are being
 * unassigned.
 * @param unassignments - A collection of asset unassignments representing an
 * asset ID and an effective unassignment date.
 */
export async function unassignAssets(
  key: string,
  unassignments: AssetUnassignment[]
): Promise<UnassignAssetsResponse> {
  const url = `/v1/project/${key}/asset`;
  const response = await api.request({
    url,
    method: 'DELETE',
    data: unassignments.map(({ assetId, date }) => ({
      assetId,
      effectiveDate: normalizeDateTime(date).toISO(),
    })),
  });

  return parseResponse(unassignAssetsResponseSchema, response.data, {
    schemaName: 'UnassignAssets',
    endpoint: url,
  });
}
